import { useState, useEffect } from 'react'
import { AllergyCheckResult, InteractionCheckResult, PrescriptionOverrideWarnings } from '../types/prescription'
import './AllergyInteractionWarnings.css'

interface AllergyInteractionWarningsProps {
  allergyCheck: AllergyCheckResult | null
  interactionCheck: InteractionCheckResult | null
  onOverrideChange?: (overrides: PrescriptionOverrideWarnings | null) => void
}

export function AllergyInteractionWarnings({
  allergyCheck,
  interactionCheck,
  onOverrideChange,
}: AllergyInteractionWarningsProps) {
  const hasWarnings = allergyCheck?.hasAllergies || interactionCheck?.hasInteractions

  // State for override reasons
  const [allergyOverrides, setAllergyOverrides] = useState<Record<string, string>>({})
  const [interactionOverrides, setInteractionOverrides] = useState<Record<string, string>>({})

  // Generate unique IDs for interactions (since they don't have IDs)
  const getInteractionId = (interaction: any, index: number) => {
    return `interaction-${interaction.medication1.name}-${interaction.medication2.name}-${index}`
  }

  // Update parent when overrides change
  useEffect(() => {
    if (!onOverrideChange) return

    const hasAllergyOverrides = allergyCheck?.hasAllergies && Object.keys(allergyOverrides).length > 0
    const hasInteractionOverrides = interactionCheck?.hasInteractions && Object.keys(interactionOverrides).length > 0

    if (hasAllergyOverrides || hasInteractionOverrides) {
      const overrides: PrescriptionOverrideWarnings = {
        allergies: allergyCheck?.hasAllergies
          ? allergyCheck.allergies
              .filter((a) => allergyOverrides[a.allergyId])
              .map((a) => ({
                allergyId: a.allergyId,
                reason: allergyOverrides[a.allergyId],
              }))
          : [],
        interactions: interactionCheck?.hasInteractions
          ? interactionCheck.interactions
              .map((interaction, idx) => ({
                interactionId: getInteractionId(interaction, idx),
                reason: interactionOverrides[getInteractionId(interaction, idx)] || '',
              }))
              .filter((i) => i.reason)
              .map((i) => ({ interactionId: i.interactionId, reason: i.reason }))
          : [],
        overriddenBy: '', // Will be set by backend
        overriddenAt: new Date().toISOString(),
      }
      onOverrideChange(overrides)
    }
    // Don't call onOverrideChange(null) - let parent handle empty state
    // This preserves dosage overrides if they exist
  }, [allergyOverrides, interactionOverrides, allergyCheck, interactionCheck, onOverrideChange])

  if (!hasWarnings) {
    return null
  }

  return (
    <div className="allergy-interaction-warnings">
      <h4>⚠️ Warnings</h4>
      <p className="warning-intro">
        The following warnings have been detected. To proceed with this prescription, you must provide a reason for
        overriding each warning.
      </p>

      {allergyCheck?.hasAllergies && (
        <div className="warning-section allergy-warnings">
          <div className="warning-header">
            <strong>⚠️ Patient Allergies Detected</strong>
          </div>
          <ul className="warning-list">
            {allergyCheck.allergies.map((allergy, idx) => (
              <li key={idx} className="warning-item">
                <div className="warning-item-header">
                  <span className="medication-name">{allergy.medication.name}</span>
                  {allergy.severity && (
                    <span className={`severity-badge severity-${allergy.severity}`}>
                      {allergy.severity}
                    </span>
                  )}
                </div>
                <div className="warning-item-details">
                  Patient has known allergy to: <strong>{allergy.triggerDisplay}</strong>
                  {allergy.category && ` (${allergy.category})`}
                </div>
                <div className="override-reason">
                  <label htmlFor={`allergy-override-${allergy.allergyId}`}>
                    Reason for override (required):
                  </label>
                  <textarea
                    id={`allergy-override-${allergy.allergyId}`}
                    value={allergyOverrides[allergy.allergyId] || ''}
                    onChange={(e) =>
                      setAllergyOverrides({
                        ...allergyOverrides,
                        [allergy.allergyId]: e.target.value,
                      })
                    }
                    placeholder="Explain why you are proceeding despite this allergy warning..."
                    required
                    rows={2}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {interactionCheck?.hasInteractions && (
        <div className="warning-section interaction-warnings">
          <div className="warning-header">
            <strong>⚠️ Drug Interactions Detected</strong>
          </div>
          <ul className="warning-list">
            {interactionCheck.interactions.map((interaction, idx) => {
              const interactionId = getInteractionId(interaction, idx)
              return (
                <li key={idx} className="warning-item">
                  <div className="warning-item-header">
                    <span className="medication-pair">
                      {interaction.medication1.name} + {interaction.medication2.name}
                    </span>
                    <span className={`severity-badge severity-${interaction.severity}`}>
                      {interaction.severity}
                    </span>
                  </div>
                  <div className="warning-item-details">
                    <div className="interaction-description">{interaction.description}</div>
                    {interaction.recommendation && (
                      <div className="interaction-recommendation">
                        <strong>Recommendation:</strong> {interaction.recommendation}
                      </div>
                    )}
                  </div>
                  <div className="override-reason">
                    <label htmlFor={`interaction-override-${interactionId}`}>
                      Reason for override (required):
                    </label>
                    <textarea
                      id={`interaction-override-${interactionId}`}
                      value={interactionOverrides[interactionId] || ''}
                      onChange={(e) =>
                        setInteractionOverrides({
                          ...interactionOverrides,
                          [interactionId]: e.target.value,
                        })
                      }
                      placeholder="Explain why you are proceeding despite this interaction warning..."
                      required
                      rows={2}
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

